/**
 * Licenses Page
 * Tech Portal interface for managing all licenses
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { DataTable } from '../../components/shared/DataTable';
import { useToast } from '../../components/shared/Toast';
import { LicenseForm } from './LicenseForm';
import { Modal } from '../../components/shared/Modal';
import { MdFilterList, MdSearch, MdRefresh, MdEdit, MdHourglassEmpty, MdBlock, MdCheckCircle } from 'react-icons/md';
import { cn } from '../../lib/utils';
import { apiFetch } from '../../utils/api';
import { ClearFiltersButton, PageSkeleton } from '@euroasiann/shared/ui';

// Usage of relative URL for flexibility
const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? '' : 'http://localhost:3000');

interface LicenseData {
  _id: string;
  licenseKey: string;
  status: 'active' | 'expired' | 'suspended' | 'revoked';
  licenseType: string;
  expiryDate: string;
  organizationId: string;
  organizationName: string;
  maxUsers: number;
  maxVessels: number;
  maxItems: number;
  currentUsage: {
    users: number;
    vessels: number;
    items: number;
    employees: number;
  };
}

export function LicensesPage() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<LicenseData | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [extendDate, setExtendDate] = useState('');

  // Fetch Licenses
  const { data: licenses, isLoading } = useQuery({
    queryKey: ['tech-licenses-list'],
    queryFn: async () => {
      const response = await apiFetch(`/api/v1/tech/licenses`);
      if (!response.ok) throw new Error('Failed to fetch licenses');
      const result = await response.json();
      return result.data as LicenseData[];
    }
  });



  const extendExpiryMutation = useMutation({
    mutationFn: async ({ id, date }: { id: string; date: string }) => {
      const response = await apiFetch(`/api/v1/tech/licenses/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ expiryDate: date })
      });
      if (!response.ok) throw new Error('Failed to extend expiry');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tech-licenses-list'] });
      setIsExtendModalOpen(false);
      showToast('License expiry extended', 'success');
    },
    onError: (err: any) => showToast(err.message, 'error')
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const response = await apiFetch(`/api/v1/tech/licenses/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status })
      });
      if (!response.ok) throw new Error('Failed to update status');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tech-licenses-list'] });
      showToast('License status updated', 'success');
    },
    onError: (err: any) => {
      console.error("Mutation error:", err);
      showToast(err.message || 'Failed to update status', 'error');
    }
  });

  // Filter Logic
  const filteredLicenses = useMemo(() => {
    if (!licenses) return [];
    let result = licenses;

    if (filterStatus !== 'all') {
      result = result.filter(l => l.status === filterStatus);
    }

    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter(l =>
        (l.organizationName || "").toLowerCase().includes(lowerQuery) ||
        (l.licenseKey || "").toLowerCase().includes(lowerQuery)
      );
    }
    return result;
  }, [licenses, filterStatus, searchQuery]);

  // Actions
  const handleEdit = (license: LicenseData) => {
    setSelectedLicense(license);
    setIsEditModalOpen(true);
  };

  const handleExtend = (license: LicenseData) => {
    setSelectedLicense(license);
    // Default to +30 days from current expiry or today
    const current = new Date(license.expiryDate);
    current.setDate(current.getDate() + 30);
    setExtendDate(current.toISOString().split('T')[0]);
    setIsExtendModalOpen(true);
  };

  const handleToggleSuspend = (license: LicenseData) => {
    // Determine the NEW target status based on the current lower-cased status
    const newStatus = license.status === 'suspended' ? 'ACTIVE' : 'SUSPENDED';
    if (confirm(`Are you sure you want to ${newStatus} this license?`)) {
      updateStatusMutation.mutate({ id: license._id, status: newStatus });
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    queryClient.invalidateQueries({ queryKey: ['tech-licenses-list'] });
    setTimeout(() => setIsRefreshing(false), 800);
  };

  // Columns
  const columns = [
    {
      key: 'organizationName',
      header: 'Organization',
      render: (l: LicenseData) => (
        <div
          onClick={() => navigate(`/licenses/${l._id}`)}
          className="text-left group cursor-pointer"
        >
          <div className="font-semibold text-[hsl(var(--foreground))] group-hover:text-blue-600 transition-colors">{l.organizationName || "Unknown"}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono">{(l.licenseKey || "").substring(0, 8)}...</div>
        </div>
      )
    },
    {
      key: 'licenseType',
      header: 'Plan',
      render: (l: LicenseData) => (
        <span className="px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-bold uppercase dark:bg-blue-900/30 dark:text-blue-300">
          {l.licenseType || "Standard"}
        </span>
      )
    },
    {
      key: 'usage',
      header: 'Usage (Users)',
      render: (l: LicenseData) => {
        const pct = l.maxUsers > 0 ? ((l.currentUsage?.users || 0) / l.maxUsers) * 100 : 0;
        const color = pct > 90 ? 'bg-red-500' : pct > 75 ? 'bg-amber-500' : 'bg-emerald-500';
        return (
          <div className="w-24">
            <div className="flex justify-between text-xs mb-1">
              <span>{l.currentUsage?.users || 0} / {l.maxUsers || 0}</span>
            </div>
            <div className="h-1.5 w-full bg-[#0088cc]/15 dark:bg-[#0088cc]/20 rounded-full overflow-hidden">
              <div className={`h-full ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
          </div>
        );
      }
    },
    {
      key: 'status',
      header: 'Status',
      render: (l: LicenseData) => {
        const colors: Record<string, string> = {
          active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300',
          expired: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
          suspended: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300',
          revoked: 'bg-[#0088cc]/10 text-[hsl(var(--foreground))] dark:bg-[#0088cc]/18 dark:text-[#0088cc]/45'
        };
        const st = l.status || "active";
        return (
          <span className={cn('px-2 py-1 rounded-full text-xs font-bold uppercase', colors[st] || colors.active)}>
            {st}
          </span>
        );
      }
    },
    {
      key: 'expiryDate',
      header: 'Expires',
      render: (l: LicenseData) => {
        const d = l.expiryDate ? new Date(l.expiryDate) : null;
        if (!d) return <span className="text-sm">N/A</span>;
        const isExpired = d < new Date();
        return (
          <span className={cn('text-sm', isExpired ? 'text-red-600 font-bold' : 'text-[hsl(var(--muted-foreground))]')}>
            {d.toLocaleDateString()}
          </span>
        );
      }
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right',
      render: (l: LicenseData) => (
        <div className="flex items-center justify-end gap-2">
          <button onClick={() => handleExtend(l)} title="Extend Expiry" className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg dark:text-blue-400 dark:hover:bg-blue-900/20">
            <MdHourglassEmpty className="w-4 h-4" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); handleEdit(l); }} title="Edit Plan" className="p-1.5 text-[hsl(var(--muted-foreground))] hover:bg-[#0088cc]/10 rounded-lg dark:text-[#0088cc]/65 dark:hover:bg-[#0088cc]/18">
            <MdEdit className="w-4 h-4" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); handleToggleSuspend(l); }} title={l.status === 'suspended' ? 'Reactivate' : 'Suspend'} className={cn("p-1.5 rounded-lg", l.status === 'suspended' ? 'text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400' : 'text-red-600 hover:bg-red-50 dark:text-red-400')}>
            {l.status === 'suspended' ? <MdCheckCircle className="w-4 h-4" /> : <MdBlock className="w-4 h-4" />}
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(var(--foreground))] mb-1">License Management</h1>
          <p className="text-[hsl(var(--muted-foreground))]">Monitor and manage all tenant organization licenses</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="p-4 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px] relative">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0088cc]/65" />
          <input
            type="text"
            placeholder="Search organization or license key..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-[hsl(var(--background))] border border-[hsl(var(--border))] focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>

        <div className="flex items-center gap-2">
          <MdFilterList className="text-[#0088cc]/65" />
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-3 py-2 rounded-lg bg-[hsl(var(--background))] border border-[hsl(var(--border))] outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        <ClearFiltersButton
          visible={searchQuery.trim() !== '' || filterStatus !== 'all'}
          onClick={() => {
            setSearchQuery('');
            setFilterStatus('all');
          }}
          variant="toolbar"
        />

        <button onClick={handleRefresh} disabled={isRefreshing} className="p-2 text-[hsl(var(--muted-foreground))] hover:bg-[#0088cc]/10 rounded-lg dark:hover:bg-[#0088cc]/18">
          <MdRefresh className={cn("w-5 h-5", isRefreshing && "animate-spin")} />
        </button>
      </div>

      {/* Table */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-6">
            <PageSkeleton variant="table" />
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={filteredLicenses}
            onRowClick={(license: LicenseData) => navigate(`/licenses/${license._id}`)}
          />
        )}
      </div>

      {/* Edit/Create Modal */}
      {isEditModalOpen && (
        <Modal
          title={selectedLicense ? "Edit License" : "Provision New License"}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
        >
          <LicenseForm
            license={selectedLicense as any} // Type casting for compatibility
            onSuccess={() => { setIsEditModalOpen(false); queryClient.invalidateQueries({ queryKey: ['tech-licenses-list'] }); }}
            onCancel={() => setIsEditModalOpen(false)}
          />
        </Modal>
      )}

      {/* Extend Modal */}
      {isExtendModalOpen && selectedLicense && (
        <Modal
          title="Extend License Expiry"
          isOpen={isExtendModalOpen}
          onClose={() => setIsExtendModalOpen(false)}
        >
          <div className="p-4 space-y-4">
            <p className="text-sm text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">
              Current Expiry: <span className="font-semibold">{new Date(selectedLicense.expiryDate).toLocaleDateString()}</span>
            </p>
            <div>
              <label className="block text-sm font-medium mb-1">New Expiry Date</label>
              <input
                type="date"
                value={extendDate}
                onChange={e => setExtendDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-[#0088cc]/18 dark:border-[#0088cc]/20"
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setIsExtendModalOpen(false)} className="px-4 py-2 text-[hsl(var(--muted-foreground))] hover:bg-[#0088cc]/10 rounded-lg">Cancel</button>
              <button
                onClick={() => extendExpiryMutation.mutate({ id: selectedLicense._id, date: new Date(extendDate).toISOString() })}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                disabled={extendExpiryMutation.isPending}
              >
                {extendExpiryMutation.isPending ? 'Saving...' : 'Confirm Extension'}
              </button>
            </div>
          </div>
        </Modal>
      )}

    </div>
  );
}
